import { computed, ref, unref, watchEffect, type MaybeRefOrGetter } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

export type TabKey = "patients" | "invites";

export type TabDef = {
  key: TabKey;
  name: string;
  icon: string;
  hidden?: boolean;
  disabled?: boolean;
};

export type EmitType = {
  kind: TabKey;
  id: string;
};

export type UseChatDockOptions = {
  onlyPatients?: MaybeRefOrGetter<boolean | undefined>;
  onlyInvites?: MaybeRefOrGetter<boolean | undefined>;
  extend?: MaybeRefOrGetter<boolean | undefined>;
};

const search = ref("");
const collapsed = ref(true);
const activeTab = ref<TabKey>("patients");
const selectedId = ref("");

let initialized = false;

/** Clears the shared dock search filter (e.g. on logout). Does not run on patient navigation. */
export function resetChatDockSearch() {
  search.value = "";
}

function clampTab(tab: TabKey, opts: { onlyPatients?: boolean; onlyInvites?: boolean }): TabKey {
  if (opts.onlyInvites) return "invites";
  if (opts.onlyPatients) return "patients";
  return tab;
}

export function useChatDock(options: UseChatDockOptions = {}) {
  const { t } = useI18n();
  const router = useRouter();
  const route = useRoute();

  const onlyPatients = computed(() => !!unref(options.onlyPatients));
  const onlyInvites = computed(() => !!unref(options.onlyInvites));
  const extend = computed(() => !!unref(options.extend));

  // init only once (first time chat dock is ever mounted)
  if (!initialized) {
    collapsed.value = !extend.value;
    activeTab.value = onlyInvites.value ? "invites" : "patients";
    initialized = true;
  }

  // keep activeTab valid for current page restrictions
  watchEffect(() => {
    activeTab.value = clampTab(activeTab.value, {
      onlyPatients: onlyPatients.value,
      onlyInvites: onlyInvites.value,
    });
  });

  watchEffect(() => {
    const path = route.path;
    const params = route.params as { id?: string };

    if (path.startsWith("/rooms/patient/") && params.id) {
      selectedId.value = params.id;
      activeTab.value = clampTab("patients", {
        onlyPatients: onlyPatients.value,
        onlyInvites: onlyInvites.value,
      });
    } else if (path.startsWith("/rooms/invites/") && params.id) {
      selectedId.value = params.id;
      activeTab.value = clampTab("invites", {
        onlyPatients: onlyPatients.value,
        onlyInvites: onlyInvites.value,
      });
    } else if (!path.startsWith("/rooms/patient/") && !path.startsWith("/rooms/invites/")) {
      selectedId.value = "";
    }
  });

  const toggleCollapsed = () => (collapsed.value = !collapsed.value);

  const tabs = computed<TabDef[]>(() => {
    const list: TabDef[] = [
      {
        key: "patients",
        name: t("generic.entity.patient", 0),
        icon: "mdi-account-group",
        hidden: onlyInvites.value,
      },
      {
        key: "invites",
        name: t("generic.entity.doctor", 0),
        icon: "mdi-flag",
        hidden: onlyPatients.value,
      },
    ];

    return list.filter((x) => !x.hidden);
  });

  const title = computed(() => tabs.value.find((x) => x.key === activeTab.value)?.name ?? "");

  const searchPlaceholder = computed(() =>
    activeTab.value === "patients"
      ? t("generic.tables.searchByNameOrEmail")
      : t("generic.tables.searchByName")
  );

  const onSelect = async (item: EmitType) => {
    // guard against null/undefined ids coming from some tabs
    selectedId.value = typeof item.id === "string" ? item.id : "";
    switch (item.kind) {
      case "patients":
        await router.push(`/rooms/patient/${item.id}`);
        break;
      case "invites":
        await router.push(`/rooms/invites/${item.id}`);
        break;
      default:
        break;
    }
  };

  return {
    // state (singleton)
    search,
    collapsed,
    activeTab,
    selectedId,

    // derived
    tabs,
    title,
    searchPlaceholder,

    // actions
    toggleCollapsed,
    onSelect,
    resetSearch: resetChatDockSearch,
  };
}
